<template>
  <section
    class="c_text_icon_card"
    :class="[`c_text_icon_card--${cardStyle}`, `c_text_icon_card--${cardTheme}`, classes]"
  >
    <div class="c_text_icon_card__content">
      <div class="c_text_icon_card__icon_container" v-if="card.icon.length">
        <img class="c_text_icon_card__icon" :src="card.icon[0].url" :alt="card.icon[0].title" />
      </div>
      <div class="c_text_icon_card__content_container">
        <h3 v-if="card.cardTitle">{{ card.cardTitle }}</h3>
        <h4 v-if="card.cardSubtitle">{{ card.cardSubtitle }}</h4>
        <div class="c_text_icon_card__description" v-html="card.cardDescription" v-if="card.cardDescription"></div>
        <a
          class="c_text_icon_card__cta"
          v-if="card.cardLink.url"
          :href="card.cardLink.url"
          :aria-label="card.cardLink.ariaLabel || card.cardLink.customText || ''"
          :target="card.cardLink.target"
        >
          <span>{{ card.cardLink.customText || card.cardLink.text }}</span>
          <IconSVG handle="chevron_right_v2" />
        </a>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, ref } from '@nuxtjs/composition-api';
import MediaImage from 'Components/image/MediaImage.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';

export default defineComponent({
  name: 'TextIconCard',
  components: {
    MediaImage,
    IconSVG,
  },
  props: {
    card: Object,
    cardStyle: { type: String, default: 'borderDividers' }, // 'card', 'cardLarge', 'borderDividers',
    classes: Object,
  },
  setup(props) {
    const cardTheme = ref('default'); // 'default', 'cerulean', 'pink', 'purple', 'blue', 'green'
    cardTheme.value = props.card?.cardTheme || 'default';
    return { cardTheme };
  },
});
</script>

<style lang="scss">
.c_text_icon_cards {
  --text-icon-card-card-bg: var(--color-black);
  --text-icon-card-card-bg-hover: var(--text-icon-card-card-bg);
  --text-icon-card-color: var(--color-white);
  --text-icon-border-color: rgba(57, 57, 57, 1);
  --text-icon-card-theme: rgb(255, 183, 0);

  &[data-component-theme='blackBackgroundWhiteText'] {
    --text-icon-card-card-bg: var(--color-black);
    --text-icon-card-card-bg-hover: rgba(57, 57, 57, 1);
    --text-icon-border-color: rgba(113, 113, 113, 1);
    --text-icon-card-theme: var(--color-yellow);
  }
  &[data-component-theme='whiteBackgroundBlackText'] {
    --text-icon-card-card-bg: var(--color-white);
    --text-icon-card-card-bg-hover: rgba(255, 237, 191, 1);
    --text-icon-card-color: var(--color-black);
    --text-icon-border-color: rgba(221, 221, 221, 1);
    --text-icon-card-theme: var(--color-red);
  }
  &[data-component-theme='darkGrayBackgroundWhiteText'] {
    --text-icon-card-card-bg: rgba(39, 39, 39, 1);
    --text-icon-card-color: var(--color-white);
    --text-icon-border-color: rgba(113, 113, 113, 1);
    --text-icon-card-theme: var(--color-yellow);
  }
  &[data-component-theme='lightGrayBackgroundBlackText'] {
    --text-icon-card-card-bg: rgba(221, 221, 221, 1);
    --text-icon-card-color: var(--color-black);
    --text-icon-border-color: rgb(201, 201, 201);
    --text-icon-card-theme: var(--color-black);

    .c_text_icon_card--card,
    .c_text_icon_card--cardLarge {
      --text-icon-card-card-bg: var(--color-white);
      --text-icon-card-theme: var(--color-red);
    }
  }
  &[data-component-theme='yellowBackgroundBlackText'] {
    --text-icon-card-card-bg: var(--color-yellow);
    --text-icon-card-color: var(--color-black);
    --text-icon-border-color: rgba(221, 221, 221, 1);
    --text-icon-card-theme: var(--color-black);
    .c_text_icon_card--card,
    .c_text_icon_card--cardLarge {
      --text-icon-card-card-bg: var(--color-white);
      --text-icon-border-color: rgba(221, 221, 221, 1);
      --text-icon-card-theme: var(--color-red);
    }
  }
}
.c_text_icon_card {
  background-color: var(--text-icon-card-card-bg);
  color: var(--text-icon-card-color);
  display: flex;
  &--card {
    border: 1px solid var(--text-icon-border-color);
    border-radius: px_to_rem(6);
    align-items: center;
  }
  &--cardLarge {
    border: 1px solid var(--text-icon-border-color);
    border-radius: px_to_rem(6);
    transition: background-color ease-in-out 0.2s;
    max-width: 543px;
    .c_text_icon_card__content {
      flex-direction: column;
      align-items: flex-start;
      gap: px_to_rem(40);
      @include mq($mq_md_to_lg) {
        align-items: center;
      }
    }
    .c_text_icon_card__icon {
      margin-bottom: 0;
    }
    @include mq($mq_md_to_lg) {
      .c_text_icon_card__content {
        flex-direction: row;
      }
    }
  }
  &--thickYellow:not(.c_text_icon_card--borderDividers) {
    border: 3px solid var(--color-yellow);
    border-radius: px_to_rem(8);
    --text-icon-card-theme: var(--color-red) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-red) !important;
    }
  }
  &--cerulean {
    --text-icon-card-theme: var(--color-cerulean-03) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-cerulean-03) !important;
    }
  }
  &--pink {
    --text-icon-card-theme: var(--color-pink-03) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-pink-03) !important;
    }
  }
  &--purple {
    --text-icon-card-theme: var(--color-purple-03) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-purple-03) !important;
    }
  }
  &--blue {
    --text-icon-card-theme: var(--color-blue-03) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-blue-03) !important;
    }
  }
  &--green {
    --text-icon-card-theme: var(--color-green-03) !important;
    a,
    a:visited,
    a:focus {
      color: var(--color-green-03) !important;
    }
  }
  &__content {
    padding: px_to_rem(32);
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    h3 {
      @include font('graphik_semibold');
      @include fs_lh(22, 30, 20, 30);
    }
    h4 {
      @include font('graphik_semibold');
      @include fs_lh(16, 24, 16, 24);
      color: var(--text-icon-card-theme);
    }
  }
  &__description {
    margin-top: px_to_rem(16);
  }
  &__cta {
    color: var(--text-icon-card-theme);
    @include font('graphik_semibold');
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: px_to_rem(10);
    padding-top: px_to_rem(26);
    margin-top: auto;
    align-self: flex-start;
    width: 100%;
    &:hover {
      text-decoration: underline;
    }
    &:focus,
    &:visited,
    &:active {
      color: var(--text-icon-card-theme);
    }
    .c_icon_svg {
      display: flex;
      flex-basis: px_to_rem(12);
      margin-right: auto;
      justify-content: flex-end;
      svg {
        width: px_to_rem(7);
        height: px_to_rem(12);
      }
    }
  }
  &__icon {
    width: auto;
    height: px_to_rem(60);
    margin-bottom: px_to_rem(16);
  }
  &__icon_container {
    flex: 0 1 100px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__content_container {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    word-break: break-word;
  }
}
</style>
